/**
 * adminShared — pequenos componentes de apresentação reutilizados entre
 * AdminClients (lista) e AdminTenantDetail (tela cheia de detalhes).
 * Extraído para módulo próprio pra evitar import circular entre os dois.
 */
import { useEffect, useState } from "react";
import { auth } from "../lib/api";

/**
 * getAdminSession — lê a sessão cacheada de /auth/admin/login (super-admin
 * real OU funcionário) e resolve o nível de acesso atual. Super-admin real
 * (db.admins) sempre tem acesso total; funcionário (kinng_staff) só enxerga
 * o que suas `permissions` liberam — usado tanto pra filtrar a sidebar
 * quanto pra decidir se a Layer 2 (financeiro) do módulo Funcionários
 * aparece.
 */
export const getAdminSession = () => {
    const cached = auth.readCachedAdminMe();
    const user = cached?.user || null;
    const isStaff = !!user?.is_staff;
    return {
        user,
        isStaff,
        isSuperAdmin: !!user && !isStaff,
        permissions: isStaff ? (user.permissions || {}) : null,
    };
};

/**
 * ConfirmModal — substitui window.confirm() em todo o painel admin: overlay
 * escuro centralizado, card no mesmo tema escuro do resto do painel,
 * fechável clicando no backdrop ou apertando Esc.
 */
export const ConfirmModal = ({
    open,
    title,
    message,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    danger = false,
    onConfirm,
    onCancel,
}) => {
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => { if (e.key === "Escape") onCancel?.(); };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div
            data-testid="admin-confirm-modal"
            onClick={onCancel}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-950 shadow-2xl p-6"
            >
                {title && <h3 className="salon-heading text-xl text-white mb-2">{title}</h3>}
                <p className="text-sm text-slate-300 leading-relaxed">{message}</p>
                <div className="mt-6 flex items-center gap-3">
                    <button
                        type="button"
                        data-testid="admin-confirm-cancel"
                        onClick={onCancel}
                        className="flex-1 min-h-[44px] rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/20 text-xs font-semibold uppercase tracking-wider transition-all"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        data-testid="admin-confirm-ok"
                        onClick={onConfirm}
                        className={`flex-1 min-h-[44px] rounded-xl text-xs font-semibold uppercase tracking-wider transition-all text-white ${
                            danger ? "bg-red-600 hover:bg-red-700" : "bg-indigo-500 hover:bg-indigo-400"
                        }`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * useConfirmModal — encapsula o estado de "qual ação está pendente de
 * confirmação". Uso:
 *   const { requestConfirm, ConfirmModalElement } = useConfirmModal();
 *   requestConfirm({ message: "...", danger: true, onConfirm: async () => {...} });
 *   return <div>...{ConfirmModalElement}</div>;
 */
export const useConfirmModal = () => {
    const [state, setState] = useState(null);

    const requestConfirm = (opts) => setState(opts);
    const close = () => setState(null);
    const handleConfirm = async () => {
        try {
            await state?.onConfirm?.();
        } finally {
            close();
        }
    };

    const ConfirmModalElement = (
        <ConfirmModal
            open={!!state}
            title={state?.title}
            message={state?.message}
            confirmLabel={state?.confirmLabel}
            cancelLabel={state?.cancelLabel}
            danger={state?.danger}
            onConfirm={handleConfirm}
            onCancel={close}
        />
    );

    return { requestConfirm, ConfirmModalElement };
};

export const PlanBadge = ({ plan }) => {
    const c =
        plan === "Premium"
            ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
            : plan === "Pro"
            ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"
            : "bg-slate-500/15 text-slate-300 border-slate-500/30";
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] uppercase tracking-wider font-bold border ${c}`}>
            {plan}
        </span>
    );
};

export const StatusBadge = ({ status }) => {
    const map = {
        active: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "Ativo", dot: "bg-emerald-400" },
        trial: { c: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Trial", dot: "bg-amber-400" },
        suspended: { c: "bg-orange-500/15 text-orange-300 border-orange-500/30", label: "Suspenso", dot: "bg-orange-400" },
        cancelled: { c: "bg-red-500/15 text-red-300 border-red-500/30", label: "Cancelado", dot: "bg-red-400" },
        deleted: { c: "bg-slate-500/15 text-slate-400 border-slate-500/30", label: "Excluído", dot: "bg-slate-500" },
    };
    const cfg = map[status] || map.trial;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${cfg.c}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot} animate-pulse`}></span>
            {cfg.label}
        </span>
    );
};

export const DetailRow = ({ label, value, mono }) => (
    <div className="flex items-start justify-between gap-4">
        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500 pt-0.5">{label}</span>
        <span className={`text-right text-white text-sm ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
);
